/**
 * SchemaFormBuilder Component
 *
 * Visual form builder for vertical schema configuration.
 * Supports user inputs, product attributes, and derived attributes.
 */

import type { AttributeType } from "@tatvex/config";
import { Plus, Trash2, ChevronDown, ChevronUp, User, Package, Calculator } from "lucide-react";
import { useCallback, useState } from "react";

import type {
  SchemaFormBuilderProps,
  SchemaFormValue,
  AttributeDefinitionValue,
  DerivedAttributeValue,
  FieldError,
} from "./types";
import { createEmptyAttributeDefinition, isValidCamelCase } from "./types";

// ============================================================================
// Attribute Type Options
// ============================================================================

const ATTRIBUTE_TYPES: readonly { value: AttributeType; label: string }[] = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "enum", label: "Enum" },
  { value: "date", label: "Date" },
  { value: "currency", label: "Currency" },
  { value: "percentage", label: "Percentage" },
  { value: "url", label: "URL" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "array", label: "Array" },
];

// ============================================================================
// Attribute Row Sub-Component
// ============================================================================

interface AttributeRowProps {
  readonly value: AttributeDefinitionValue;
  readonly onChange: (value: AttributeDefinitionValue) => void;
  readonly onDelete: () => void;
  readonly existingKeys?: readonly string[];
  readonly readOnly?: boolean;
}

function AttributeRow({
  value,
  onChange,
  onDelete,
  existingKeys = [],
  readOnly = false,
}: AttributeRowProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);

  const keyIsValid = !value.key || isValidCamelCase(value.key);
  const keyIsDuplicate = existingKeys.filter((k) => k === value.key).length > 1;

  const handleFieldChange = useCallback(
    <K extends keyof AttributeDefinitionValue>(key: K, newValue: AttributeDefinitionValue[K]) => {
      onChange({ ...value, [key]: newValue });
    },
    [value, onChange]
  );

  return (
    <div className="attribute-row rounded-md border border-gray-200 bg-white">
      {/* Collapsed row */}
      <div
        className={`flex cursor-pointer items-center gap-2 p-3 ${isExpanded ? "border-b border-gray-100" : ""}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Key */}
        <input
          type="text"
          value={value.key}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange("key", e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          readOnly={readOnly}
          placeholder="attributeKey"
          className={`w-40 rounded border px-2 py-1 font-mono text-sm ${
            !keyIsValid || keyIsDuplicate ? "border-red-300 bg-red-50" : "border-gray-300"
          }`}
        />

        {/* Type */}
        <select
          value={value.type}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange("type", e.target.value as AttributeType);
          }}
          onClick={(e) => e.stopPropagation()}
          disabled={readOnly}
          className="w-28 rounded border border-gray-300 px-2 py-1 text-sm"
        >
          {ATTRIBUTE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        {/* Label */}
        <input
          type="text"
          value={value.label}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange("label", e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          readOnly={readOnly}
          placeholder="Display Label"
          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
        />

        {/* Required */}
        <label className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={value.required}
            onChange={(e) => handleFieldChange("required", e.target.checked)}
            disabled={readOnly}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-xs text-gray-600">Req</span>
        </label>

        {/* Expand/Delete */}
        <div className="flex items-center gap-1">
          {!readOnly && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="rounded p-1 text-gray-400 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="space-y-3 bg-gray-50 p-3">
          {/* Validation errors */}
          {(!keyIsValid || keyIsDuplicate) && (
            <div className="text-sm text-red-500">
              {!keyIsValid && "Key must be camelCase (e.g., myAttribute)"}
              {keyIsDuplicate && "Key already exists"}
            </div>
          )}

          {/* Description */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Description</label>
            <input
              type="text"
              value={value.description ?? ""}
              onChange={(e) => handleFieldChange("description", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="Optional description..."
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>

          {/* Default Value */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Default Value</label>
            <input
              type="text"
              value={value.defaultValue !== undefined ? String(value.defaultValue) : ""}
              onChange={(e) => {
                const val = e.target.value;
                let parsed: unknown = val;
                if (val === "true") parsed = true;
                else if (val === "false") parsed = false;
                else if (val === "") parsed = undefined;
                else if (!isNaN(Number(val))) parsed = Number(val);
                handleFieldChange("defaultValue", parsed);
              }}
              readOnly={readOnly}
              placeholder="null"
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>

          {/* Type-specific constraints hint */}
          <div className="text-xs text-gray-500">
            {value.type === "enum" && (
              <p>Configure enum options in the JSON view (constraints.options)</p>
            )}
            {value.type === "number" && (
              <p>Configure min/max/step in the JSON view (constraints.min, max, step)</p>
            )}
            {value.type === "string" && (
              <p>Configure length limits in the JSON view (constraints.minLength, maxLength)</p>
            )}
            {value.type === "currency" && (
              <p>Configure currency code in the JSON view (constraints.currency)</p>
            )}
            {value.type === "array" && (
              <p>Configure item type in the JSON view (constraints.itemType)</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Derived Attribute Row Sub-Component
// ============================================================================

interface DerivedAttributeRowProps {
  readonly value: DerivedAttributeValue;
  readonly onChange: (value: DerivedAttributeValue) => void;
  readonly onDelete: () => void;
  readonly availableDependencies: readonly string[];
  readonly readOnly?: boolean;
}

function DerivedAttributeRow({
  value,
  onChange,
  onDelete,
  availableDependencies,
  readOnly = false,
}: DerivedAttributeRowProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFieldChange = useCallback(
    <K extends keyof DerivedAttributeValue>(key: K, newValue: DerivedAttributeValue[K]) => {
      onChange({ ...value, [key]: newValue });
    },
    [value, onChange]
  );

  const handleDependenciesChange = useCallback(
    (text: string) => {
      const deps = text
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      handleFieldChange("dependencies", deps);
    },
    [handleFieldChange]
  );

  return (
    <div className="derived-row rounded-md border border-yellow-200 bg-yellow-50">
      {/* Collapsed row */}
      <div
        className={`flex cursor-pointer items-center gap-2 p-3 ${isExpanded ? "border-b border-yellow-200" : ""}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Calculator className="h-4 w-4 text-yellow-600" />

        {/* Key */}
        <input
          type="text"
          value={value.key}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange("key", e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          readOnly={readOnly}
          placeholder="derivedKey"
          className="w-40 rounded border border-gray-300 px-2 py-1 font-mono text-sm"
        />

        {/* Type */}
        <select
          value={value.type}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange("type", e.target.value as AttributeType);
          }}
          onClick={(e) => e.stopPropagation()}
          disabled={readOnly}
          className="w-28 rounded border border-gray-300 px-2 py-1 text-sm"
        >
          {ATTRIBUTE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        {/* Label */}
        <input
          type="text"
          value={value.label}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange("label", e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          readOnly={readOnly}
          placeholder="Display Label"
          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
        />

        {/* Expand/Delete */}
        <div className="flex items-center gap-1">
          {!readOnly && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="rounded p-1 text-gray-400 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="space-y-3 bg-yellow-50/50 p-3">
          {/* Compute Expression */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Compute Expression <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={value.computeExpression}
              onChange={(e) => handleFieldChange("computeExpression", e.target.value)}
              readOnly={readOnly}
              placeholder="price * quantity"
              className="rounded border border-gray-300 px-2 py-1 font-mono text-sm"
            />
          </div>

          {/* Dependencies */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Dependencies (comma-separated) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={value.dependencies.join(", ")}
              onChange={(e) => handleDependenciesChange(e.target.value)}
              readOnly={readOnly}
              placeholder="price, quantity"
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
            {availableDependencies.length > 0 && (
              <p className="text-xs text-gray-500">
                Available: {availableDependencies.slice(0, 10).join(", ")}
                {availableDependencies.length > 10 && "..."}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Description</label>
            <input
              type="text"
              value={value.description ?? ""}
              onChange={(e) => handleFieldChange("description", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="Optional description..."
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Attribute Section Sub-Component
// ============================================================================

interface AttributeSectionProps {
  readonly title: string;
  readonly icon: React.ElementType;
  readonly iconColor: string;
  readonly attributes: readonly AttributeDefinitionValue[];
  readonly onChange: (attributes: readonly AttributeDefinitionValue[]) => void;
  readonly readOnly?: boolean;
}

function AttributeSection({
  title,
  icon: Icon,
  iconColor,
  attributes,
  onChange,
  readOnly = false,
}: AttributeSectionProps): React.JSX.Element {
  const allKeys = attributes.map((a) => a.key);

  const handleAttributeChange = useCallback(
    (index: number, newAttr: AttributeDefinitionValue) => {
      const newAttrs = [...attributes];
      newAttrs[index] = newAttr;
      onChange(newAttrs);
    },
    [attributes, onChange]
  );

  const handleAddAttribute = useCallback(() => {
    onChange([...attributes, createEmptyAttributeDefinition()]);
  }, [attributes, onChange]);

  const handleDeleteAttribute = useCallback(
    (index: number) => {
      onChange(attributes.filter((_, i) => i !== index));
    },
    [attributes, onChange]
  );

  return (
    <div className="attribute-section rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${iconColor}`} />
          <h4 className="font-medium text-gray-900">{title}</h4>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            {attributes.length}
          </span>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={handleAddAttribute}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus className="h-4 w-4" />
            Add Attribute
          </button>
        )}
      </div>

      <div className="space-y-2 p-4">
        {/* Column headers */}
        <div className="hidden text-xs font-medium text-gray-500 md:grid md:grid-cols-[160px,112px,1fr,48px,64px] md:gap-2 md:px-3">
          <span>Key</span>
          <span>Type</span>
          <span>Label</span>
          <span></span>
          <span></span>
        </div>

        {attributes.map((attr, index) => (
          <AttributeRow
            key={attr.key || index}
            value={attr}
            onChange={(a) => handleAttributeChange(index, a)}
            onDelete={() => handleDeleteAttribute(index)}
            existingKeys={allKeys}
            readOnly={readOnly}
          />
        ))}

        {attributes.length === 0 && (
          <p className="py-4 text-center text-sm text-gray-500">
            No attributes defined. {!readOnly && "Add attributes to define the schema."}
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * SchemaFormBuilder - Visual schema configuration
 *
 * CSS Classes:
 * - schema-form: Container element
 * - schema-form__header: Header with vertical info
 * - schema-form__sections: Attribute sections
 */
export function SchemaFormBuilder({
  value,
  onChange,
  onValidate,
  readOnly = false,
  errors = [],
  className = "",
  showDerivedAttributes = true,
}: SchemaFormBuilderProps): React.JSX.Element {
  const [localErrors, setLocalErrors] = useState<readonly FieldError[]>([]);
  // Merge errors from external and local validation
  void [...errors, ...localErrors];

  const handleChange = useCallback(
    <K extends keyof SchemaFormValue>(key: K, newValue: SchemaFormValue[K]) => {
      const updated: SchemaFormValue = {
        ...value,
        [key]: newValue,
      };

      if (onValidate) {
        const result = onValidate(updated);
        setLocalErrors(result.errors);
      }

      onChange(updated);
    },
    [value, onChange, onValidate]
  );

  const handleAddDerivedAttribute = useCallback(() => {
    const newDerived: DerivedAttributeValue = {
      key: "",
      type: "number",
      label: "",
      required: false,
      computed: true,
      computeExpression: "",
      dependencies: [],
    };
    handleChange("derivedAttributes", [...(value.derivedAttributes ?? []), newDerived]);
  }, [value.derivedAttributes, handleChange]);

  const handleDerivedChange = useCallback(
    (index: number, newDerived: DerivedAttributeValue) => {
      const derivedAttrs = [...(value.derivedAttributes ?? [])];
      derivedAttrs[index] = newDerived;
      handleChange("derivedAttributes", derivedAttrs);
    },
    [value.derivedAttributes, handleChange]
  );

  const handleDeleteDerived = useCallback(
    (index: number) => {
      const derivedAttrs = (value.derivedAttributes ?? []).filter((_, i) => i !== index);
      handleChange("derivedAttributes", derivedAttrs.length > 0 ? derivedAttrs : undefined);
    },
    [value.derivedAttributes, handleChange]
  );

  // Available dependencies for derived attributes (product attributes + earlier derived)
  const availableDependencies = [
    ...value.productAttributes.map((a) => a.key),
    ...(value.derivedAttributes ?? []).map((a) => a.key),
  ];

  return (
    <div className={`schema-form flex flex-col gap-6 ${className}`}>
      {/* Header Info */}
      <div className="schema-form__header rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Vertical Key</label>
            <input
              type="text"
              value={value.verticalKey}
              onChange={(e) => handleChange("verticalKey", e.target.value)}
              readOnly={readOnly}
              placeholder="vertical-key"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Version</label>
            <input
              type="number"
              value={value.version}
              onChange={(e) => handleChange("version", parseInt(e.target.value, 10) || 1)}
              readOnly={readOnly}
              min={1}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Display Name</label>
            <input
              type="text"
              value={value.displayName ?? ""}
              onChange={(e) => handleChange("displayName", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="Health Insurance"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              value={value.description ?? ""}
              onChange={(e) => handleChange("description", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="Optional description..."
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Attribute Sections */}
      <div className="schema-form__sections space-y-6">
        {/* User Inputs */}
        <AttributeSection
          title="User Inputs"
          icon={User}
          iconColor="text-blue-500"
          attributes={value.userInputs}
          onChange={(attrs) => handleChange("userInputs", attrs)}
          readOnly={readOnly}
        />

        {/* Product Attributes */}
        <AttributeSection
          title="Product Attributes"
          icon={Package}
          iconColor="text-green-500"
          attributes={value.productAttributes}
          onChange={(attrs) => handleChange("productAttributes", attrs)}
          readOnly={readOnly}
        />

        {/* Derived Attributes */}
        {showDerivedAttributes && (
          <div className="derived-section rounded-lg border border-yellow-200 bg-yellow-50/50">
            <div className="flex items-center justify-between border-b border-yellow-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-yellow-600" />
                <h4 className="font-medium text-gray-900">Derived Attributes</h4>
                <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800">
                  {value.derivedAttributes?.length ?? 0}
                </span>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={handleAddDerivedAttribute}
                  className="inline-flex items-center gap-1 text-sm text-yellow-700 hover:text-yellow-900"
                >
                  <Plus className="h-4 w-4" />
                  Add Derived
                </button>
              )}
            </div>

            <div className="space-y-2 p-4">
              {(value.derivedAttributes ?? []).map((derived, index) => (
                <DerivedAttributeRow
                  key={derived.key || index}
                  value={derived}
                  onChange={(d) => handleDerivedChange(index, d)}
                  onDelete={() => handleDeleteDerived(index)}
                  availableDependencies={availableDependencies}
                  readOnly={readOnly}
                />
              ))}

              {(!value.derivedAttributes || value.derivedAttributes.length === 0) && (
                <p className="py-4 text-center text-sm text-gray-500">
                  No derived attributes defined.{" "}
                  {!readOnly && "Add computed attributes based on other attributes."}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export type { SchemaFormBuilderProps };
